import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { motion, useScroll, useTransform } from "framer-motion";
import { Helmet } from "react-helmet-async";
import Marquee from "react-fast-marquee";
import { ArrowDown, ArrowUpRight, Volume2, VolumeX, Play, Quote, Star, PenLine } from "lucide-react";
import { useLang, UI } from "@/i18n/lang";
import { PROJECTS, WHAT_I_BUILD, PROCESS, ADVANTAGES, LOGO } from "@/content/site";
import { SERVICE_LANDINGS } from "@/content/serviceLandings";
import { Reveal, MaskedLines, MaskedLinesInView } from "@/lib/motion";
import ProjectCard from "@/components/ProjectCard";
import ReviewForm from "@/components/ReviewForm";
import { Seo } from "@/components/Seo";
import { LocaleLink as Link, useLocaleRoute, localizePath, coreAlternates } from "@/i18n/routes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EASE = [0.33, 1, 0.68, 1];

function Hero() {
  const { t } = useLang();
  const ref = useRef(null);
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const markY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const markOpacity = useTransform(scrollYProgress, [0, 0.8], [1, 0.2]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    let cancelled = false;
    const tryPlay = () =>
      v.play().then(() => { if (!cancelled) setNeedsTap(false); }).catch(() => {});
    tryPlay();
    // Retry once shortly after mount in case the first attempt raced with loading.
    const t1 = setTimeout(tryPlay, 500);
    // If autoplay is blocked (e.g. Safari Low Power Mode), offer an explicit play button.
    const t2 = setTimeout(() => { if (!cancelled && v.paused) setNeedsTap(true); }, 1600);
    return () => { cancelled = true; clearTimeout(t1); clearTimeout(t2); };
  }, []);

  const reveal = () => setPlaying(true);

  const handleTapPlay = () => {
    const v = videoRef.current;
    if (!v) return;
    v.play().then(() => setNeedsTap(false)).catch(() => {});
  };

  const toggleMute = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) {
      v.volume = 0.5;
      v.play().catch(() => {});
    }
  };

  const FEATHER = "radial-gradient(ellipse 72% 72% at 50% 48%, #000 60%, transparent 100%)";

  return (
    <section ref={ref} className="relative pt-32 md:pt-40 lg:pt-44 pb-20 lg:pb-28 overflow-hidden" data-testid="hero-section">
      <div className="pointer-events-none absolute top-1/4 right-0 w-[700px] h-[700px] rounded-full bg-bronze/10 blur-[140px]" />
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="flex items-center gap-3 mb-10"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-bronze opacity-60" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-bronze" />
          </span>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone">
            {t(UI.home.availability)}
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <h1 className="font-serif font-medium text-ink text-5xl sm:text-6xl md:text-7xl lg:text-[6rem] leading-[0.98] tracking-[-0.03em]">
              <MaskedLines lines={t(UI.home.heroLines)} />
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
              className="mt-8 max-w-lg text-base md:text-lg leading-relaxed text-stone"
            >
              {t(UI.home.heroSub)}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.75 }}
              className="mt-7 flex flex-wrap gap-2.5"
            >
              {t(UI.home.heroChips).map((chip) => (
                <span key={chip} className="rounded-full border border-border px-4 py-1.5 text-[12px] text-ink/70 font-mono">
                  {chip}
                </span>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.85 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <Link
                to="/work"
                data-testid="hero-cta-work"
                className="group inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-8 py-4 text-sm font-semibold tracking-wide hover:bg-[#f0be6e] transition-colors duration-300"
              >
                {t(UI.cta.viewWork)}
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-cta-project"
                className="rounded-full border border-ink/25 text-ink px-8 py-4 text-sm font-semibold tracking-wide hover:bg-ink hover:text-paper transition-colors duration-300"
              >
                {t(UI.cta.startProject)}
              </Link>
            </motion.div>
          </div>

          {/* Brand reveal — the W logo is the fallback; the looping video reveals on top as soon as it can play */}
          <div className="lg:col-span-5 relative flex items-center justify-center">
            <motion.div style={{ y: markY, opacity: markOpacity }} className="relative w-full max-w-[600px] mx-auto aspect-video">
              <img
                src={LOGO.mark}
                alt="WERKVERDUNG"
                className="absolute inset-0 w-full h-full object-contain select-none"
                style={{ WebkitMaskImage: FEATHER, maskImage: FEATHER }}
              />
              <video
                ref={videoRef}
                data-testid="hero-reveal-video"
                className="absolute inset-0 w-full h-full object-cover select-none transition-opacity duration-700"
                style={{ WebkitMaskImage: FEATHER, maskImage: FEATHER, opacity: playing ? 1 : 0 }}
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                onLoadedData={reveal}
                onCanPlay={reveal}
                onPlaying={() => { reveal(); setNeedsTap(false); }}
              >
                {/* mp4 (H.264) first — universally supported incl. Safari/iOS. webm (VP9) as fallback. */}
                <source src={LOGO.reveal} type="video/mp4" />
                <source src={LOGO.revealWebm} type="video/webm" />
              </video>
              {needsTap && (
                <button
                  data-testid="hero-play"
                  onClick={handleTapPlay}
                  aria-label="Video abspielen"
                  className="absolute inset-0 z-20 flex items-center justify-center group"
                >
                  <span className="flex h-16 w-16 items-center justify-center rounded-full bg-bronze/90 text-paper shadow-lg backdrop-blur-sm transition-transform duration-300 group-hover:scale-110">
                    <Play size={26} className="ml-1" fill="currentColor" />
                  </span>
                </button>
              )}
              <button
                data-testid="hero-unmute"
                onClick={toggleMute}
                aria-label={muted ? "Ton aktivieren" : "Stumm"}
                className="absolute bottom-2 right-2 z-10 p-2.5 rounded-full text-ink/60 hover:text-bronze transition-colors"
              >
                {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
            </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-16 flex items-center gap-3 text-stone"
        >
          <ArrowDown size={16} className="animate-bounce" />
          <span className="font-mono text-[11px] uppercase tracking-[0.2em]">{t(UI.home.selectedWork)}</span>
        </motion.div>
      </div>
    </section>
  );
}

function MarqueeBand() {
  const { t } = useLang();
  const text = t(UI.home.marquee);
  return (
    <section className="py-8 border-y border-border overflow-hidden bg-[#0C0C0E]" data-testid="marquee">
      <Marquee speed={40} gradient={false}>
        <span className="font-serif italic text-6xl md:text-8xl text-ink/[0.07] pr-8">{`${text} ${text} `}</span>
      </Marquee>
    </section>
  );
}

function SelectedWork() {
  const { t } = useLang();
  const feature = PROJECTS[0];
  const rest = PROJECTS.slice(1, 6);
  return (
    <section className="py-24 md:py-32 lg:py-40" data-testid="selected-work">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">01 — {t(UI.home.selectedWork)}</span>
              <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink max-w-xl leading-[1.05]">
                {t(UI.home.selectedWorkSub)}
              </h2>
            </div>
            <Link to="/work" className="hidden md:inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-widest text-ink hover:text-bronze transition-colors">
              {t(UI.cta.viewWork)} <ArrowUpRight size={14} />
            </Link>
          </div>
        </Reveal>

        <Reveal>
          <div className="mb-16">
            <ProjectCard project={feature} aspect="aspect-[16/10] md:aspect-[21/9]" index={0} />
          </div>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-16">
          {rest.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 2) * 0.08}>
              <ProjectCard project={p} aspect="aspect-[4/3]" index={i + 1} />
            </Reveal>
          ))}
        </div>

        <div className="mt-16 md:hidden">
          <Link to="/work" className="inline-flex items-center gap-2 font-mono text-[12px] uppercase tracking-widest text-bronze">
            {t(UI.cta.viewWork)} <ArrowUpRight size={14} />
          </Link>
        </div>
      </div>
    </section>
  );
}

function WhatIBuild() {
  const { t, lang } = useLang();
  const BUILD_TO_LANDING = { websites: "website", apps: "app", publishing: "kdp", "digital-products": "digital-product" };
  const landingPath = (id) => {
    const s = SERVICE_LANDINGS.find((x) => x.key === BUILD_TO_LANDING[id]);
    return s ? s[lang].path : "/services";
  };
  return (
    <section className="py-24 md:py-32 border-t border-border" data-testid="what-i-build">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">02 — {t(UI.home.whatIBuild)}</span>
        </Reveal>
        <div className="mt-8 border-t border-border">
          {WHAT_I_BUILD.map((row, i) => (
            <Reveal key={row.id} delay={i * 0.05}>
              <Link
                to={landingPath(row.id)}
                data-testid={`build-row-${row.id}`}
                className="group relative grid grid-cols-1 md:grid-cols-12 items-center gap-4 py-8 md:py-10 border-b border-border overflow-hidden"
              >
                <div className="absolute inset-0 bg-bronze translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.33,1,0.68,1)] -z-0" />
                <span className="relative z-10 md:col-span-1 font-mono text-xs text-bronze group-hover:text-paper transition-colors duration-500">0{i + 1}</span>
                <h3 className="relative z-10 md:col-span-4 font-serif text-4xl md:text-5xl text-ink group-hover:text-paper transition-colors duration-500">
                  {row.title}
                </h3>
                <p className="relative z-10 md:col-span-5 text-sm md:text-base text-stone group-hover:text-paper/80 transition-colors duration-500 max-w-md">
                  {t(row.desc)}
                </p>
                <div className="relative z-10 md:col-span-2 flex justify-start md:justify-end">
                  <div className="w-14 h-14 rounded-full border border-border group-hover:border-paper/40 flex items-center justify-center transition-colors duration-500">
                    <ArrowUpRight size={18} className="text-ink group-hover:text-paper transition-colors duration-500" />
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowIWork() {
  const { t } = useLang();
  return (
    <section className="py-24 md:py-32 bg-[#0C0C0E]" data-testid="how-i-work">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="mb-16 max-w-xl">
            <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">03 — {t(UI.home.howIWork)}</span>
            <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-ink leading-[1.05]">
              {t(UI.home.howIWorkSub)}
            </h2>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-px bg-border border border-border">
          {PROCESS.map((step, i) => (
            <Reveal key={step.n} delay={i * 0.07}>
              <div className="bg-paper p-8 md:p-6 lg:p-8 h-full min-h-[240px] flex flex-col justify-between group hover:bg-bronze transition-colors duration-500">
                <span className="font-serif italic text-6xl lg:text-7xl text-bronze group-hover:text-paper transition-colors duration-500">{step.n}</span>
                <div>
                  <h3 className="font-display font-bold text-sm uppercase tracking-widest text-ink group-hover:text-paper transition-colors duration-500">
                    {t(step.title)}
                  </h3>
                  <p className="mt-2 text-sm text-stone group-hover:text-paper/80 transition-colors duration-500">{t(step.desc)}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function WhyWorkWithMe() {
  const { t } = useLang();
  return (
    <section className="py-24 md:py-32 lg:py-40 border-t border-border" data-testid="why-work">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">04 — {t(UI.home.whyTitle)}</span>
            </Reveal>
            <h2 className="mt-6 font-serif text-3xl md:text-4xl lg:text-[2.9rem] leading-[1.15] tracking-tight text-ink">
              <MaskedLinesInView
                lines={t(UI.home.whyStatement).split(". ").map((s, idx, arr) => (idx < arr.length - 1 ? s + "." : s))}
              />
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
              {ADVANTAGES.map((adv, i) => (
                <Reveal key={i} delay={i * 0.05}>
                  <div className="py-6 border-b border-border flex items-baseline gap-4">
                    <span className="font-mono text-xs text-bronze">0{i + 1}</span>
                    <span className="font-serif text-2xl md:text-[1.7rem] text-ink leading-tight">{t(adv)}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stars({ value = 0, size = 15 }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${value} / 5`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={size}
          className={n <= value ? "text-bronze" : "text-border"}
          fill={n <= value ? "currentColor" : "none"}
        />
      ))}
    </span>
  );
}

const SERVICE_ORDER = ["Website", "App", "Digital Product", "Book / Publishing", "Other"];

function Testimonials() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");

  const load = () => {
    axios
      .get(`${API}/testimonials`)
      .then(({ data }) => setItems(Array.isArray(data) ? data : []))
      .catch(() => setItems([]));
  };

  useEffect(() => {
    load();
  }, []);

  const serviceLabel = (s) => t(UI.testimonials.services[s] || UI.testimonials.services.Other);

  const empty = items.length === 0;
  const categories = SERVICE_ORDER.filter((s) => items.some((it) => it.service === s));
  const showFilter = categories.length > 1;
  const visible = filter === "all" ? items : items.filter((it) => it.service === filter);

  // schema.org AggregateRating + Review JSON-LD for rich snippets
  const avg = items.length
    ? (items.reduce((sum, it) => sum + (it.rating || 0), 0) / items.length).toFixed(1)
    : null;
  const schema = items.length
    ? {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "WERKVERDUNG",
        url: "https://werkverdung.com",
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: avg,
          reviewCount: String(items.length),
          bestRating: "5",
          worstRating: "1",
        },
        review: items.slice(0, 20).map((it) => ({
          "@type": "Review",
          author: { "@type": "Person", name: it.name },
          datePublished: (it.created_at || "").slice(0, 10),
          reviewRating: { "@type": "Rating", ratingValue: String(it.rating), bestRating: "5", worstRating: "1" },
          reviewBody: it.quote,
        })),
      }
    : null;

  return (
    <section className="py-24 md:py-32 border-t border-border" data-testid="testimonials">
      {schema && (
        <Helmet>
          <script type="application/ld+json">{JSON.stringify(schema)}</script>
        </Helmet>
      )}
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        <Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-8">
            <div>
              <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">— {t(UI.testimonials.eyebrow)}</span>
              <h2 className="mt-6 font-serif text-4xl md:text-6xl lg:text-7xl tracking-tight text-ink leading-[1.02] max-w-3xl">
                {t(UI.testimonials.title)}
              </h2>
            </div>
            <button
              data-testid="open-review-form"
              onClick={() => setOpen(true)}
              className="group inline-flex shrink-0 items-center gap-2 self-start rounded-full border border-ink/25 text-ink px-7 py-3.5 text-sm font-semibold tracking-wide hover:bg-ink hover:text-paper transition-colors duration-300"
            >
              <PenLine size={16} />
              {t(UI.testimonials.writeBtn)}
            </button>
          </div>
        </Reveal>

        {showFilter && (
          <Reveal>
            <div className="mt-10 flex flex-wrap gap-2.5" data-testid="testimonials-filter">
              {["all", ...categories].map((c) => (
                <button
                  key={c}
                  data-testid={`testimonials-filter-${c === "all" ? "all" : c.replace(/[^a-zA-Z]/g, "").toLowerCase()}`}
                  onClick={() => setFilter(c)}
                  className={`rounded-full border px-4 py-1.5 text-[12px] font-mono transition-colors duration-300 ${
                    filter === c
                      ? "border-bronze bg-bronze text-paper"
                      : "border-border text-ink/70 hover:border-bronze"
                  }`}
                >
                  {c === "all" ? t(UI.testimonials.filterAll) : serviceLabel(c)}
                </button>
              ))}
            </div>
          </Reveal>
        )}

        {empty ? (
          <Reveal>
            <div data-testid="testimonials-empty" className="mt-16 border-l border-border/60 pl-7 md:pl-9 max-w-xl">
              <Quote size={26} className="text-bronze/60" aria-hidden="true" />
              <p className="mt-4 font-serif text-2xl md:text-3xl leading-[1.35] text-ink/70">
                {t(UI.testimonials.previewNote)}
              </p>
              <button
                data-testid="empty-review-cta"
                onClick={() => setOpen(true)}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-7 py-3.5 text-sm font-semibold tracking-wide hover:bg-[#f0be6e] transition-colors duration-300"
              >
                <PenLine size={16} />
                {t(UI.testimonials.writeBtn)}
              </button>
            </div>
          </Reveal>
        ) : (
          <div className="mt-16 grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-14">
            {visible.map((item, i) => (
              <Reveal key={item.id || i} delay={(i % 2) * 0.08}>
                <figure
                  data-testid={`testimonial-${i}`}
                  className="relative flex h-full flex-col border-l border-bronze/50 pl-7 md:pl-9"
                >
                  <div className="flex items-center justify-between">
                    <Quote size={26} className="text-bronze/70" aria-hidden="true" />
                    <Stars value={item.rating} />
                  </div>
                  <blockquote className="mt-4 font-serif text-xl md:text-2xl leading-[1.4] text-ink/90">
                    {item.quote}
                  </blockquote>
                  <figcaption className="mt-7 flex items-center gap-4">
                    <span className="flex h-11 w-11 items-center justify-center rounded-full border border-border bg-secondary font-serif text-sm text-stone">
                      {item.name?.[0] || "—"}
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-ink leading-tight">{item.name}</p>
                      <p className="text-[13px] text-stone leading-tight">
                        {[item.company, serviceLabel(item.service)].filter(Boolean).join(" · ")}
                      </p>
                    </div>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        )}
      </div>

      <ReviewForm open={open} onClose={() => setOpen(false)} onSubmitted={load} />
    </section>
  );
}

function FinalCTA() {
  const { t } = useLang();
  return (
    <section className="relative py-28 md:py-40 bg-[#0C0C0E] overflow-hidden" data-testid="final-cta">
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
        <div className="w-[900px] h-[500px] rounded-full bg-bronze/15 blur-[160px]" />
      </div>
      <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20 text-center">
        <Reveal>
          <span className="font-mono text-[11px] uppercase tracking-[0.2em] text-bronze">05 — {t(UI.cta.startProject)}</span>
        </Reveal>
        <h2 className="mt-8 font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[1] tracking-tight max-w-4xl mx-auto text-ink">
          <MaskedLinesInView lines={[t(UI.home.finalTitle)]} />
        </h2>
        <Reveal delay={0.15}>
          <p className="mt-8 text-base md:text-lg text-stone max-w-xl mx-auto">{t(UI.home.finalText)}</p>
          <Link
            to="/contact"
            data-testid="final-cta-button"
            className="mt-12 inline-flex items-center gap-2 rounded-full bg-bronze text-paper px-10 py-5 text-sm font-semibold tracking-wide hover:bg-[#f0be6e] transition-colors duration-300 gold-glow"
          >
            {t(UI.cta.letsStart)} <ArrowUpRight size={16} />
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

export default function Home({ locale = "de" }) {
  useLocaleRoute(locale, localizePath("/", locale === "de" ? "en" : "de"));
  return (
    <>
      <Seo
        title={UI.seo.home.title[locale]}
        description={UI.seo.home.desc[locale]}
        path={localizePath("/", locale)}
        lang={locale}
        alternates={coreAlternates("/")}
      />
      <Hero />
      <MarqueeBand />
      <SelectedWork />
      <WhatIBuild />
      <HowIWork />
      <WhyWorkWithMe />
      <Testimonials />
      <FinalCTA />
    </>
  );
}
